import { 
  NOTIFICATION_TYPES, 
  FILE_UPLOAD_CONFIG 
} from '../constants/notificationConstants';

// Validate required fields based on notification type
export const validateNotificationForm = (data) => {
  const errors = {};

  // Common validation
  if (!data.recipientIds || data.recipientIds.length === 0) {
    errors.recipients = 'Please select at least one recipient';
  }

  // Type-specific validation
  switch (data.type) {
    case NOTIFICATION_TYPES.GENERAL:
      if (!data.content || !data.content.trim()) {
        errors.content = 'Message content is required';
      }
      break;

    case NOTIFICATION_TYPES.INVITATION:
      if (!data.content || !data.content.trim()) {
        errors.content = 'Message content is required';
      }
      if (!data.venue || !data.venue.trim()) {
        errors.venue = 'Venue is required';
      }
      if (!data.eventDate) {
        errors.eventDate = 'Event date is required';
      }
      if (!data.eventTime) {
        errors.eventTime = 'Event time is required';
      }
      break;

    case NOTIFICATION_TYPES.MEETING:
      if (!data.meetingHeading || !data.meetingHeading.trim()) {
        errors.meetingHeading = 'Meeting heading is required';
      }
      if (!data.chairedBy || !data.chairedBy.trim()) {
        errors.chairedBy = 'Chaired by field is required';
      }
      if (!data.venue || !data.venue.trim()) {
        errors.venue = 'Venue is required';
      }
      if (!data.eventDate) {
        errors.eventDate = 'Date is required';
      }
      if (!data.eventTime) {
        errors.eventTime = 'Time is required';
      }
      if (!data.description || !data.description.trim()) {
        errors.description = 'Description is required';
      }
      break;

    default:
      errors.type = 'Invalid notification type';
  }

  // Validate future date for events/meetings
  if ((data.type === NOTIFICATION_TYPES.INVITATION || data.type === NOTIFICATION_TYPES.MEETING) && data.eventDate) {
    const eventDate = new Date(data.eventDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (eventDate < today) {
      errors.eventDate = 'Event date cannot be in the past';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validate file uploads
export const validateFile = (file) => {
  const errors = [];

  // Check file size
  if (file.size > FILE_UPLOAD_CONFIG.maxSize) {
    errors.push(`File size must be less than ${FILE_UPLOAD_CONFIG.maxSize / (1024 * 1024)}MB`);
  }

  // Check file type
  if (!FILE_UPLOAD_CONFIG.allowedTypes.includes(file.type)) {
    errors.push('File type not supported. Please upload images, PDFs, or Excel files only.');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate multiple files
export const validateFiles = (files) => {
  const errors = [];

  // Check file count
  if (files.length > FILE_UPLOAD_CONFIG.maxFiles) {
    errors.push(`Maximum ${FILE_UPLOAD_CONFIG.maxFiles} files allowed`);
    return { isValid: false, errors };
  }

  // Validate each file
  files.forEach((file, index) => {
    const fileValidation = validateFile(file);
    if (!fileValidation.isValid) {
      errors.push(`File ${index + 1}: ${fileValidation.errors.join(', ')}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Sanitize notification data
export const sanitizeNotificationData = (data) => {
  const sanitized = { ...data };

  // Trim string fields
  if (sanitized.content) sanitized.content = sanitized.content.trim();
  if (sanitized.venue) sanitized.venue = sanitized.venue.trim();
  if (sanitized.meetingHeading) sanitized.meetingHeading = sanitized.meetingHeading.trim();
  if (sanitized.chairedBy) sanitized.chairedBy = sanitized.chairedBy.trim();
  if (sanitized.description) sanitized.description = sanitized.description.trim();

  // Remove empty arrays
  if (sanitized.attachments && sanitized.attachments.length === 0) {
    delete sanitized.attachments;
  }

  // Remove null/undefined values
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === null || sanitized[key] === undefined || sanitized[key] === '') {
      delete sanitized[key];
    }
  });

  return sanitized;
};

// Get validation requirements for each tab
export const getValidationRequirements = (type) => {
  const requirements = {
    recipients: 'At least one recipient must be selected'
  };

  switch (type) {
    case NOTIFICATION_TYPES.GENERAL:
      requirements.content = 'Message content is required';
      break;

    case NOTIFICATION_TYPES.INVITATION:
      requirements.content = 'Message content is required';
      requirements.venue = 'Venue is required';
      requirements.eventDate = 'Event date is required';
      requirements.eventTime = 'Event time is required';
      break;

    case NOTIFICATION_TYPES.MEETING:
      requirements.meetingHeading = 'Meeting heading is required';
      requirements.chairedBy = 'Chaired by field is required';
      requirements.venue = 'Venue is required';
      requirements.eventDate = 'Date is required';
      requirements.eventTime = 'Time is required';
      requirements.description = 'Description is required';
      break;
  }

  return requirements;
};